import React, { useRef } from 'react';
import { Download, Globe, Mail, Phone, MapPin, Star, Users, Award, Clock, Heart, Sparkles, Flag } from 'lucide-react';
import html2pdf from 'html2pdf.js';

const BusinessProfile = () => {
  const profileRef = useRef(null);
  const BRAND_BLUE = '#141A3D';

  const handleDownloadPDF = () => {
    const element = profileRef.current;
    const opt = {
      margin: 0,
      filename: 'Mervin-Travel-Business-Profile.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Download Button - Fixed */}
      <button
        onClick={handleDownloadPDF}
        className="fixed top-6 right-6 z-50 hover:scale-105 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all duration-300"
        style={{ backgroundColor: BRAND_BLUE }}
        data-testid="download-pdf-button"
      >
        <Download size={20} />
        <span className="font-semibold">Download PDF</span>
      </button>

      <div ref={profileRef}>
        {/* Hero Section - Creative & Elegant Design */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white" data-testid="hero-section">
          {/* Background Image with Elegant Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1572280075160-be1ab588d4d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxJc3RhbmJ1bCUyMEJvc3Bob3J1cyUyMHN1bnNldHxlbnwwfHx8fDE3NjI4NzQ3ODd8MA&ixlib=rb-4.1.0&q=85"
              alt="Istanbul Bosphorus"
              className="w-full h-full object-cover"
            />
            {/* Elegant gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-blue-50/90 to-transparent"></div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23141A3D" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')` }}></div>
          </div>

          {/* Content Container */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
            <div className="text-center">
              {/* Animated Logo Container with Dynamic Effects */}
              <div className="mb-12 animate-fade-in">
                <div className="relative inline-block">
                  {/* Gold accent behind logo */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent blur-3xl transform scale-110"></div>
                  
                  {/* Logo */}
                  <img
                    src="https://customer-assets.emergentagent.com/job_fee10e30-b6cd-4687-a390-788b0f04fc3d/artifacts/simd6crw_Logos%20%281%29.png"
                    alt="Mervin Travel Logo"
                    className="h-40 mx-auto relative z-10 drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Elegant Typography with Multi-tone Text */}
              <div className="space-y-6 mb-12">
                <h1 className="text-6xl md:text-7xl font-bold leading-tight tracking-tight">
                  <span className="block" style={{ color: BRAND_BLUE }}>Your Premier</span>
                  <span className="block bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600 bg-clip-text text-transparent">
                    B2B Travel Partner
                  </span>
                </h1>
                
                <div className="h-1 w-24 mx-auto rounded-full" style={{ background: `linear-gradient(to right, ${BRAND_BLUE}, #D4AF37, ${BRAND_BLUE})` }}></div>
                
                <p className="text-2xl md:text-3xl font-light leading-relaxed max-w-3xl mx-auto" style={{ color: BRAND_BLUE, opacity: 0.85 }}>
                  Connecting the world through
                  <span className="block mt-2 font-semibold" style={{ color: '#D4AF37' }}>
                    authentic Turkish experiences
                  </span>
                </p>
              </div>

              {/* Tagline with Elegant Styling */}
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/30 to-transparent blur-xl"></div>
                <p className="relative text-xl md:text-2xl italic font-serif px-8 py-4" style={{ color: BRAND_BLUE }}>
                  "Crafting authentic journeys with precision and heart"
                </p>
              </div>

              {/* Decorative Elements */}
              <div className="mt-16 flex justify-center gap-8 items-center">
                <div className="h-px w-16 md:w-32" style={{ background: `linear-gradient(to right, transparent, ${BRAND_BLUE})` }}></div>
                <div className="flex gap-2">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#D4AF37' }}></div>
                  <div className="w-2 h-2 rounded-full animate-pulse delay-100" style={{ backgroundColor: BRAND_BLUE }}></div>
                  <div className="w-2 h-2 rounded-full animate-pulse delay-200" style={{ backgroundColor: '#D4AF37' }}></div>
                </div>
                <div className="h-px w-16 md:w-32" style={{ background: `linear-gradient(to left, transparent, ${BRAND_BLUE})` }}></div>
              </div>

              {/* Global Offices Badge */}
              <div className="mt-12 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/80 backdrop-blur-sm shadow-lg border-2" style={{ borderColor: BRAND_BLUE, opacity: 0.9 }}>
                <Globe size={20} style={{ color: '#D4AF37' }} />
                <span className="font-semibold text-sm" style={{ color: BRAND_BLUE }}>
                  Istanbul • Dubai • United States
                </span>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
            <div className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2" style={{ borderColor: BRAND_BLUE, opacity: 0.5 }}>
              <div className="w-1 h-3 rounded-full animate-pulse" style={{ backgroundColor: '#D4AF37' }}></div>
            </div>
          </div>
        </section>

        {/* About Us Section - Merged */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-blue-50/30 relative overflow-hidden" data-testid="about-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold mb-12 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>About Us</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                  <span className="text-2xl font-semibold block mb-4">Founded in Istanbul, Türkiye,</span> 
                  Mervin Travel has grown into a globally recognized travel company with <span className="font-semibold">offices in Dubai, Türkiye, and the United States</span>. Our international presence reflects years of building trust, cultivating partnerships, and delivering exceptional travel experiences across continents.
                </p>
                <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                  Since our establishment in 2014, we have specialized in <span className="font-semibold">B2B travel services</span> throughout Turkey, creating an extensive network of <span className="font-semibold">direct hotel contracts</span> that enable us to offer rates 5-10% lower than online travel agencies—while providing exclusive perks and personalized attention that set us apart.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: BRAND_BLUE }}>
                  We maintain privileged access to world-class properties including <span className="font-semibold">Mandarin Oriental, The Peninsula, Rixos, Four Seasons, Kempinski, St. Regis, Shangri-La,</span> and <span className="font-semibold">The Bodrum Edition</span>—complemented by round-the-clock support and deep local expertise that transforms every journey into an unforgettable experience.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img src="https://images.unsplash.com/photo-1692581339924-5d0f583e39f9" alt="Istanbul Architecture" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
                <img src="https://images.unsplash.com/photo-1696024695509-0e32a9b9973f" alt="Turkish Riviera" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
                <img src="https://images.unsplash.com/photo-1696024812055-c926147c946c" alt="Coastal Landscape" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
                <img src="https://images.unsplash.com/photo-1657188237796-445fed3f0fdb" alt="Turkish Culture" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 px-6 bg-white" data-testid="mission-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold mb-12 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Our Mission</span>
            </h2>
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-3xl shadow-xl p-12" style={{ borderLeft: `8px solid ${BRAND_BLUE}` }}>
              <div className="flex items-start gap-6 mb-8">
                <div className="p-4 rounded-2xl" style={{ backgroundColor: BRAND_BLUE }}>
                  <Star className="text-white" size={32} />
                </div>
                <div>
                  <p className="text-lg leading-relaxed" style={{ color: BRAND_BLUE }}>
                    Our mission is to <span className="font-bold">redefine excellence in B2B travel</span> by offering exclusive direct hotel partnerships, rates that outperform online platforms, and unwavering support that ensures every client journey is flawless from start to finish.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-6">
                <div className="p-4 rounded-2xl" style={{ backgroundColor: BRAND_BLUE }}>
                  <Heart className="text-white" size={32} />
                </div>
                <div>
                  <p className="text-lg leading-relaxed" style={{ color: BRAND_BLUE }}>
                    We are committed to delivering <span className="font-bold">authentic Turkish experiences</span> that blend rich cultural heritage with sophisticated hospitality. Through our deep local knowledge and global perspective, we create journeys that inspire connection, discovery, and transformation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-50/50 to-gray-50 relative overflow-hidden" data-testid="values-section">
          <div className="max-w-6xl mx-auto relative z-10">
            <h2 className="text-5xl font-bold mb-12 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Our Values</span>
            </h2>
            <p className="text-xl text-center mb-16" style={{ color: BRAND_BLUE }}>
              We believe in delivering remarkable travel experiences through innovation, personalized service, and strong industry partnerships.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: Star, title: 'Client-First Excellence', desc: 'Every journey is crafted to surpass expectations, with meticulous attention to detail and unwavering commitment to satisfaction.' },
                { icon: Award, title: 'Integrity & Trust', desc: 'Building enduring partnerships through transparency, reliability, and delivering on every promise we make.' },
                { icon: Globe, title: 'Authentic Experiences', desc: 'Connecting travelers with the true essence of Turkey—from ancient heritage to contemporary culture and culinary artistry.' },
                { icon: Users, title: 'Proactive Solutions', desc: 'Anticipating needs and resolving challenges swiftly, ensuring seamless travel experiences at every step.' },
                { icon: Sparkles, title: 'Elevated Standards', desc: 'Curating sophisticated accommodations, exclusive access, and VIP services that define true hospitality.' },
                { icon: Clock, title: 'Always Available', desc: 'Round-the-clock global support ensuring your clients are never alone, regardless of time zone or location.' }
              ].map((value, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-opacity-30" style={{ borderColor: BRAND_BLUE }}>
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: BRAND_BLUE }}>
                    <value.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: BRAND_BLUE }}>{value.title}</h3>
                  <p className="leading-relaxed" style={{ color: BRAND_BLUE, opacity: 0.8 }}>{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-6 bg-white" data-testid="services-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-5xl font-bold mb-12 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Our Services</span>
            </h2>
            <p className="text-xl text-center mb-16 max-w-4xl mx-auto" style={{ color: BRAND_BLUE }}>
              Mervin Travel offers a comprehensive portfolio of sophisticated travel solutions, exclusively designed for B2B partners seeking exceptional experiences throughout Türkiye.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { title: 'Hotel Reservations', desc: 'Direct partnerships delivering rates 5-10% below market, complemented by VIP amenities and complimentary upgrades at Turkey\'s finest properties.', icon: '🏨' },
                { title: 'Bespoke Itineraries', desc: 'Fully customized journeys featuring private yacht charters, culinary expeditions, traditional hammam rituals, and guided historical explorations.', icon: '🗺️' },
                { title: 'Premium Transfers', desc: 'Executive chauffeur services, luxury vehicle fleet, and private yacht transportation for distinguished travel experiences.', icon: '🚗' },
                { title: 'Curated Experiences', desc: 'Exclusive activities from Cappadocia adventures to private wine tastings, archaeological discoveries, and artisan shopping excursions.', icon: '✨' },
                { title: 'MICE & Group Travel', desc: 'We handle corporate events, incentive trips, and large groups with the same precision and care as individual journeys. From conferences to retreats, seamless execution is guaranteed.', icon: '🎯' },
                { title: '24/7 Concierge', desc: 'Round-the-clock WhatsApp support line providing instant assistance and seamless issue resolution at any hour.', icon: '📱' },
                { title: 'Expert Local Guides', desc: 'Professional multilingual specialists who illuminate Turkish heritage, culture, and contemporary life with authentic insight.', icon: '👥' },
                { title: 'Cultural Immersion', desc: 'Deep-dive experiences connecting travelers with Turkey\'s soul—from local traditions to hidden gems known only to insiders.', icon: '🎭' },
                { title: 'Travel Documentation', desc: 'Streamlined visa processing and comprehensive travel documentation support ensuring effortless entry and smooth journeys.', icon: '📄' },
                { title: 'Transportation Solutions', desc: 'End-to-end booking services for flights, rail journeys, and cruise arrangements throughout your clients\' itineraries.', icon: '✈️' }
              ].map((service, idx) => (
                <div key={idx} className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 hover:scale-105" style={{ borderColor: BRAND_BLUE }}>
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: BRAND_BLUE }}>{service.title}</h3>
                  <p className="leading-relaxed" style={{ color: BRAND_BLUE, opacity: 0.85 }}>{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Expertise Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-50/50 to-gray-50 relative overflow-hidden" data-testid="expertise-section">
          <div className="max-w-6xl mx-auto relative z-10">
            <h2 className="text-5xl font-bold mb-12 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Why Choose Us</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-opacity-20" style={{ borderColor: BRAND_BLUE }}>
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: BRAND_BLUE }}>
                  <Globe className="text-white" size={40} />
                </div>
                <h3 className="text-3xl font-bold mb-6" style={{ color: BRAND_BLUE }}>Global Expertise & Cultural Fluency</h3>
                <p className="text-lg leading-relaxed mb-4" style={{ color: BRAND_BLUE }}>
                  Our <span className="font-semibold">multilingual sales and support team spans three continents</span>, speaking over seven languages and serving clients with deep cultural sensitivity—from Europe to the Middle East and South Asia.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: BRAND_BLUE, opacity: 0.85 }}>
                  This global perspective, combined with our Turkish roots, enables us to bridge cultures seamlessly—understanding nuanced expectations, communication styles, and hospitality preferences that vary across markets.
                </p>
              </div>
              <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-opacity-20" style={{ borderColor: BRAND_BLUE }}>
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: BRAND_BLUE }}>
                  <Heart className="text-white" size={40} />
                </div>
                <h3 className="text-3xl font-bold mb-6" style={{ color: BRAND_BLUE }}>Trusted Partnerships Worldwide</h3>
                <p className="text-lg leading-relaxed mb-4" style={{ color: BRAND_BLUE }}>
                  Trusted by agencies from <span className="font-semibold">United States, Great Britain, Saudi Arabia, Pakistan, India, and United Arab Emirates</span>, we've built our reputation on delivering experiences that honor diverse cultural values while showcasing Turkey's finest offerings.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: BRAND_BLUE, opacity: 0.85 }}>
                  Whether serving European sophistication, Middle Eastern hospitality expectations, or South Asian travel preferences, we adapt with expertise and genuine cultural understanding.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'Exclusive Partnerships', desc: 'Direct contracts with top-tier hotels ensuring superior rates and exclusive benefits', icon: '🤝' },
                { title: 'World-Class Properties', desc: 'Privileged access to Rixos, Four Seasons, Kempinski, St. Regis, Radisson Blu, Concorde Deluxe', icon: '⭐' },
                { title: 'Technology-Enabled', desc: 'Advanced AI and digital systems powering seamless operations and personalized experiences', icon: '🤖' }
              ].map((item, idx) => (
                <div key={idx} className="text-center bg-white rounded-2xl p-8 shadow-lg">
                  <div className="text-6xl mb-4">{item.icon}</div>
                  <h4 className="text-xl font-bold mb-3" style={{ color: BRAND_BLUE }}>{item.title}</h4>
                  <p style={{ color: BRAND_BLUE, opacity: 0.8 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GlowMeets Section - Rose Gold Theme */}
        <section className="py-20 px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #B76E79 0%, #F5EAEA 100%)' }} data-testid="glowmeets-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <img
                src="https://customer-assets.emergentagent.com/job_fee10e30-b6cd-4687-a390-788b0f04fc3d/artifacts/nobd2qs6_Glow%20meets%20logo_Updated%20.PNG"
                alt="GlowMeets Logo"
                className="h-24 mx-auto mb-8"
              />
              <h2 className="text-4xl font-bold mb-4" style={{ color: BRAND_BLUE }}>
                Weddings & Events by Mervin Travel
              </h2>
              <div className="h-1 w-48 mx-auto mb-8" style={{ background: 'linear-gradient(to right, #B76E79, #D4AF37)' }}></div>
              <p className="text-xl max-w-4xl mx-auto leading-relaxed" style={{ color: BRAND_BLUE }}>
                GlowMeets is the creative events and weddings arm of Mervin Travel. We design elevated celebrations, corporate conferences, and destination experiences that blend elegance with cultural authenticity.
              </p>
            </div>

            {/* Image Grid - Conference, Wedding, Group Celebration */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1718224326658-489bbfbeb2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjb25mZXJlbmNlJTIwZ2FsYSUyMHN0YWdlfGVufDB8fHx8MTc2MjkzNjk1Mnww&ixlib=rb-4.1.0&q=85" 
                  alt="Corporate Conference & Gala" 
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1700062069869-0c59ff21fa3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB3ZWRkaW5nJTIwY2VyZW1vbnl8ZW58MHx8fHwxNzYyOTM2OTQwfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Luxury Wedding Ceremony" 
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1758523981334-4b7d5e179efa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxncm91cCUyMGNlbGVicmF0aW9ufGVufDB8fHx8MTc2MjkzNjk3OXww&ixlib=rb-4.1.0&q=85" 
                  alt="Group Celebration" 
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>

            {/* Additional Event Images */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1762201732755-b59e912de5d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWRkaW5nJTIwY2VyZW1vbnl8ZW58MHx8fHwxNzYyOTM2OTQwfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Elegant Wedding Scene" 
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300">
                <img 
                  src="https://images.pexels.com/photos/16120243/pexels-photo-16120243.jpeg" 
                  alt="Luxury Event Setup" 
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Content Card with Rose-Gold Accents */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-12 border-2" style={{ borderColor: '#B76E79' }}>
              <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                From intimate corporate retreats to spectacular destination weddings, <span className="font-semibold">GlowMeets</span> orchestrates unforgettable experiences that blend meticulous planning with artistic vision, Turkish elegance with international sophistication.
              </p>
              <p className="text-lg leading-relaxed mb-8" style={{ color: BRAND_BLUE, opacity: 0.85 }}>
                Our dedicated events team handles every detail—from venue selection and design concepts to logistics and on-site coordination—ensuring flawless execution that leaves lasting impressions.
              </p>
              
              {/* Service Categories */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 rounded-2xl shadow-md" style={{ background: 'linear-gradient(135deg, #F5EAEA 0%, #FFFFFF 100%)' }}>
                  <div className="text-4xl mb-3">🎯</div>
                  <h4 className="font-bold mb-2 text-xl" style={{ color: BRAND_BLUE }}>Corporate Events</h4>
                  <p style={{ color: BRAND_BLUE, opacity: 0.7 }}>Conferences, incentives & MICE solutions</p>
                </div>
                <div className="text-center p-6 rounded-2xl shadow-md" style={{ background: 'linear-gradient(135deg, #F5EAEA 0%, #FFFFFF 100%)' }}>
                  <div className="text-4xl mb-3">💐</div>
                  <h4 className="font-bold mb-2 text-xl" style={{ color: BRAND_BLUE }}>Destination Weddings</h4>
                  <p style={{ color: BRAND_BLUE, opacity: 0.7 }}>Bespoke luxury ceremonies</p>
                </div>
                <div className="text-center p-6 rounded-2xl shadow-md" style={{ background: 'linear-gradient(135deg, #F5EAEA 0%, #FFFFFF 100%)' }}>
                  <div className="text-4xl mb-3">✨</div>
                  <h4 className="font-bold mb-2 text-xl" style={{ color: BRAND_BLUE }}>Gala Celebrations</h4>
                  <p style={{ color: BRAND_BLUE, opacity: 0.7 }}>Elegant special occasions</p>
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <a 
                  href="https://glowmeets.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-semibold text-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #B76E79 0%, #8B5A68 100%)' }}
                  data-testid="glowmeets-cta-button"
                >
                  <span>Explore GlowMeets Weddings & Events</span>
                  <span className="text-2xl">→</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Founder's Message Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-50/50 to-gray-50 relative overflow-hidden" data-testid="founder-section">
          <div className="max-w-6xl mx-auto relative z-10">
            <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Founder's Message</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-12 items-start">
              <div className="md:col-span-1">
                <div className="bg-white rounded-3xl p-8 shadow-xl text-center border-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                  <div className="w-56 h-56 rounded-full mx-auto mb-6 overflow-hidden bg-gray-100">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_fee10e30-b6cd-4687-a390-788b0f04fc3d/artifacts/yr9nh2xz_WhatsApp%20Image%202024-12-20%20at%2019.09.19_c5584368.jpg"
                      alt="Ahmet Kazancı"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ color: BRAND_BLUE }}>AHMET KAZANCI</h3>
                  <p className="text-lg mb-1" style={{ color: BRAND_BLUE, opacity: 0.7 }}>Founder & CEO</p>
                  <p className="mb-4" style={{ color: '#D4AF37', fontWeight: 600 }}>Mervin Travel</p>
                  <a 
                    href="https://www.linkedin.com/in/ahmetkazanci/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold hover:opacity-90 transition-all duration-300"
                    style={{ backgroundColor: '#0A66C2' }}
                    data-testid="founder-linkedin-link"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    <span>LinkedIn Profile</span>
                  </a>
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                    With <span className="font-semibold">over 15 years of global tourism leadership</span>, Ahmet Kazancı has shaped Mervin Travel into a bridge between cultures—combining advanced automation with heartfelt hospitality. His vision continues through GlowMeets, where creativity and connection define every celebration.
                  </p>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                    At Mervin Travel, our mission has always been to <span className="font-semibold">make every journey an experience with lasting impact</span>—one that transcends typical travel and creates authentic connections between people and places. We are redefining what personalized travel means in today's world.
                  </p>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                    I have had the privilege of helping individuals and organizations explore the world through cultural immersion, sophisticated getaways, and corporate events. Our philosophy centers on <span className="font-semibold">authenticity, excellence, and innovation</span>—values that guide every decision we make.
                  </p>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: BRAND_BLUE }}>
                    We call it <span className="font-italic">"The Art of Personalized Travel."</span> By combining cutting-edge technology with genuine human expertise, we create tailor-made experiences that feel both effortless and extraordinary. Our use of <span className="font-semibold">AI for customization and virtual previews</span> ensures precision, while our personal touch guarantees heart.
                  </p>
                  <p className="text-lg leading-relaxed" style={{ color: BRAND_BLUE }}>
                    We are committed to delivering exceptional travel experiences that <span className="font-semibold">connect people with destinations</span> in meaningful ways. For us, travel is not just about reaching a place—it's about discovery, connection, and transformation. I look forward to crafting unforgettable experiences for you and your clients.
                  </p>
                  <div className="mt-8 pt-6 border-t-2 border-opacity-20" style={{ borderColor: BRAND_BLUE }}>
                    <p className="text-2xl font-serif italic" style={{ color: '#D4AF37' }}>"Crafting authentic journeys with precision and heart."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partners Section - Redesigned */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-50/50 via-white to-gray-50 relative overflow-hidden" data-testid="partners-section">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23141A3D" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')` }}></div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <h2 className="text-5xl font-bold mb-6 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Our Partners</span>
            </h2>
            <p className="text-xl text-center mb-16" style={{ color: BRAND_BLUE, opacity: 0.8 }}>
              Connecting global agencies with world-class hospitality
            </p>

            {/* Trusted by Agencies Worldwide - First with Creative Design */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h3 className="text-4xl font-bold mb-4" style={{ color: BRAND_BLUE }}>
                  Trusted by Agencies Worldwide
                </h3>
                <div className="h-1 w-24 mx-auto mb-6 rounded-full" style={{ background: `linear-gradient(to right, ${BRAND_BLUE}, #D4AF37, ${BRAND_BLUE})` }}></div>
                <p className="text-lg max-w-3xl mx-auto" style={{ color: BRAND_BLUE, opacity: 0.85 }}>
                  Partnering with leading travel agencies across continents
                </p>
              </div>

              {/* Creative Flag Display with Dynamic Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
                {[
                  { country: 'United States', flag: '🇺🇸', gradient: 'from-blue-600 to-red-600' },
                  { country: 'Great Britain', flag: '🇬🇧', gradient: 'from-blue-700 to-red-700' },
                  { country: 'Saudi Arabia', flag: '🇸🇦', gradient: 'from-green-600 to-green-800' },
                  { country: 'Pakistan', flag: '🇵🇰', gradient: 'from-green-600 to-emerald-700' },
                  { country: 'India', flag: '🇮🇳', gradient: 'from-orange-500 to-green-600' },
                  { country: 'United Arab Emirates', flag: '🇦🇪', gradient: 'from-red-600 to-green-700' }
                ].map((item, idx) => (
                  <div 
                    key={idx} 
                    className="group relative"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    {/* Animated card with elegant hover */}
                    <div className="relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-opacity-10 hover:border-opacity-30 group-hover:scale-105 group-hover:-rotate-1" style={{ borderColor: BRAND_BLUE }}>
                      {/* Gradient accent on hover */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                      
                      {/* Content */}
                      <div className="relative p-6 text-center">
                        {/* Large flag with shadow */}
                        <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-500 filter drop-shadow-lg">
                          {item.flag}
                        </div>
                        
                        {/* Country name */}
                        <h4 className="font-bold text-sm leading-tight" style={{ color: BRAND_BLUE }}>
                          {item.country}
                        </h4>
                        
                        {/* Decorative dot */}
                        <div className="mt-3 flex justify-center">
                          <div className="w-2 h-2 rounded-full group-hover:scale-150 transition-transform duration-500" style={{ backgroundColor: '#D4AF37' }}></div>
                        </div>
                      </div>
                      
                      {/* Subtle shine effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Global reach indicator */}
              <div className="mt-12 text-center">
                <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white shadow-lg border-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                  <Globe size={24} style={{ color: '#D4AF37' }} className="animate-pulse" />
                  <span className="font-semibold text-lg" style={{ color: BRAND_BLUE }}>
                    Serving Partners Across 6 Countries
                  </span>
                </div>
              </div>
            </div>

            {/* Hotel Partners Section - Second with Elegant Layout */}
            <div>
              <div className="text-center mb-12">
                <h3 className="text-4xl font-bold mb-4" style={{ color: BRAND_BLUE }}>
                  Premium Hotel Partners
                </h3>
                <div className="h-1 w-24 mx-auto mb-6 rounded-full" style={{ background: `linear-gradient(to right, ${BRAND_BLUE}, #D4AF37, ${BRAND_BLUE})` }}></div>
                <p className="text-lg max-w-3xl mx-auto" style={{ color: BRAND_BLUE, opacity: 0.85 }}>
                  Exclusive partnerships with the world's most prestigious luxury hotels
                </p>
              </div>

              {/* Hotel cards with elegant design */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
                {[
                  'Rixos Hotels', 
                  'Four Seasons', 
                  'Kempinski', 
                  'St. Regis', 
                  'Radisson Blu', 
                  'Concorde Deluxe', 
                  'Shangri-La', 
                  'The Peninsula'
                ].map((partner, idx) => (
                  <div 
                    key={idx} 
                    className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-8 text-center border-2 border-opacity-0 hover:border-opacity-20 relative overflow-hidden"
                    style={{ 
                      borderColor: '#D4AF37',
                      animationDelay: `${idx * 0.05}s`
                    }}
                  >
                    {/* Gold accent line */}
                    <div className="absolute top-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ backgroundColor: '#D4AF37' }}></div>
                    
                    {/* Hotel name */}
                    <p className="font-bold text-base leading-tight group-hover:text-opacity-100 transition-all duration-300" style={{ color: BRAND_BLUE, opacity: 0.9 }}>
                      {partner}
                    </p>
                    
                    {/* Star icon on hover */}
                    <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Star size={16} className="mx-auto" style={{ color: '#D4AF37' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Partnership statement */}
              <div className="mt-12 text-center max-w-3xl mx-auto">
                <div className="bg-gradient-to-r from-transparent via-blue-100/50 to-transparent p-8 rounded-2xl">
                  <p className="text-lg italic" style={{ color: BRAND_BLUE }}>
                    "Through our direct partnerships, we deliver rates 5-10% lower than OTAs, complemented by exclusive VIP amenities and personalized service."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-50/50 to-gray-50" data-testid="contact-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: BRAND_BLUE }}>
              <span className="border-b-4 pb-2" style={{ borderColor: BRAND_BLUE }}>Contact Us</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                <h3 className="text-3xl font-bold mb-8" style={{ color: BRAND_BLUE }}>Get in Touch</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_BLUE }}>
                      <Phone size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm mb-1" style={{ color: BRAND_BLUE, opacity: 0.7 }}>WhatsApp</p>
                      <a href="https://wa.me/905441181827" className="text-xl font-semibold hover:opacity-80 transition-opacity" style={{ color: BRAND_BLUE }}>+90 544 118 18 27</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_BLUE }}>
                      <Mail size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm mb-1" style={{ color: BRAND_BLUE, opacity: 0.7 }}>Email</p>
                      <a href="mailto:info@mervintravel.com" className="text-xl font-semibold hover:opacity-80 transition-opacity" style={{ color: BRAND_BLUE }}>info@mervintravel.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_BLUE }}>
                      <Globe size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm mb-2" style={{ color: BRAND_BLUE, opacity: 0.7 }}>Websites</p>
                      <a href="https://www.mervintravel.com" target="_blank" rel="noopener noreferrer" className="block text-lg font-semibold hover:opacity-80 transition-opacity mb-2" style={{ color: BRAND_BLUE }}>
                        www.mervintravel.com
                      </a>
                      <a href="https://glowmeets.com/" target="_blank" rel="noopener noreferrer" className="block text-lg font-semibold hover:opacity-80 transition-opacity" style={{ color: '#B76E79' }}>
                        glowmeets.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                <h3 className="text-3xl font-bold mb-8" style={{ color: BRAND_BLUE }}>Global Offices</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 pb-6 border-b-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                    <div className="p-3 rounded-xl flex-shrink-0" style={{ backgroundColor: '#D4AF37' }}>
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm mb-1 flex items-center gap-2 font-semibold" style={{ color: BRAND_BLUE }}>
                        <span className="text-2xl">🇹🇷</span> Türkiye Office
                      </p>
                      <p className="font-semibold mb-1" style={{ color: BRAND_BLUE }}>Premier Kampüs Ofis, D:29/B, Suite 437</p>
                      <p className="font-semibold" style={{ color: BRAND_BLUE }}>Kağıthane, Istanbul, Türkiye</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 pb-6 border-b-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
                    <div className="p-3 rounded-xl flex-shrink-0" style={{ backgroundColor: '#D4AF37' }}>
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm mb-1 flex items-center gap-2 font-semibold" style={{ color: BRAND_BLUE }}>
                        <span className="text-2xl">🇦🇪</span> Dubai Office
                      </p>
                      <p className="font-semibold mb-1" style={{ color: BRAND_BLUE }}>OneJLT, 3rd Floor</p>
                      <p className="font-semibold" style={{ color: BRAND_BLUE }}>Jumeirah Lakes Towers, Dubai, UAE</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl flex-shrink-0" style={{ backgroundColor: '#D4AF37' }}>
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm mb-1 flex items-center gap-2 font-semibold" style={{ color: BRAND_BLUE }}>
                        <span className="text-2xl">🇺🇸</span> United States Office
                      </p>
                      <p className="font-semibold mb-1" style={{ color: BRAND_BLUE }}>1111B South Governors Avenue</p>
                      <p className="font-semibold" style={{ color: BRAND_BLUE }}>6892 Dover, DE 19904, USA</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center border-2 border-opacity-10" style={{ borderColor: BRAND_BLUE }}>
              <p className="text-2xl italic font-serif mb-6" style={{ color: '#D4AF37' }}>
                "Let us elevate your clients' travel experience with our expertise, local connections, and passion for Turkish hospitality."
              </p>
              <div className="h-1 w-64 mx-auto mb-6" style={{ background: 'linear-gradient(to right, #D4AF37, #C9A869, #D4AF37)' }}></div>
              <p className="text-lg mb-6" style={{ color: BRAND_BLUE }}>
                🌍 Serving partners worldwide from <span className="font-bold">three continents</span>
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <a href="https://www.linkedin.com/in/ahmetkazanci/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold hover:opacity-90 transition-all" style={{ backgroundColor: '#0A66C2' }}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  <span>LinkedIn</span>
                </a>
                <a href="https://wa.me/905441181827" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold hover:opacity-90 transition-all" style={{ backgroundColor: '#25D366' }}>
                  <Phone size={20} />
                  <span>WhatsApp</span>
                </a>
                <a href="https://www.mervintravel.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold hover:opacity-90 transition-all" style={{ backgroundColor: BRAND_BLUE }}>
                  <Globe size={20} />
                  <span>Website</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-6 border-t-4" style={{ backgroundColor: BRAND_BLUE, borderColor: '#D4AF37' }}>
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-gray-400">© 2025 Mervin Travel. All rights reserved.</p>
            <p className="mt-2 italic" style={{ color: '#D4AF37' }}>Crafting authentic journeys with precision and heart.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BusinessProfile;